/*
  FAQ accordion.

  The FAQ page's markup lives in the page content (rich text) in the Shopify
  admin, not in a theme file. It ships styles that key off an `open` class
  (`.faq-item.open .faq-answer`, `.faq-item.open .arrow`) but no script that
  ever adds that class, so the questions were inert. This supplies the missing
  behaviour without needing the page content to change.

  The questions are plain divs rather than buttons, so the keyboard and
  screen-reader affordances a <button> would give for free are added here too.

  Product and collection FAQs use native <details>/<summary> and are untouched.
*/
(function () {
  'use strict';

  var ITEM = 'faq-item';
  var OPEN = 'open';

  function answerFor(item) {
    return item.querySelector('.faq-answer');
  }

  function isOpen(item) {
    return item.classList.contains(OPEN);
  }

  // Once the opening transition has finished the height cap is lifted entirely.
  // A measured pixel height is only ever a snapshot: the page's CSS animates
  // padding-top alongside max-height, so a height read at click time is short
  // by the padding and clips the last line. Releasing the cap afterwards also
  // means reflows from font swaps, zooming or a resize can never clip either.
  function release(answer) {
    if (answer.closest('.' + ITEM).classList.contains(OPEN)) {
      answer.style.maxHeight = 'none';
    }
  }

  function setOpen(item, open) {
    var answer = answerFor(item);

    item.classList.toggle(OPEN, open);

    var question = item.querySelector('.faq-question');
    if (question) question.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!answer) return;

    answer.classList.toggle(OPEN, open);

    if (open) {
      // Animate up to the measured height; `release` lifts the cap at the end.
      answer.style.maxHeight = answer.scrollHeight + 'px';

      // transitionend is the normal path. The timer covers the cases where it
      // never fires — reduced-motion, a hidden tab, or a transition that was
      // never actually applied — so the panel can't be left capped short.
      clearTimeout(answer._faqTimer);
      answer._faqTimer = setTimeout(function () {
        release(answer);
      }, 600);
    } else {
      // Closing from `none` has nothing to animate from, so pin the current
      // height, force a reflow to commit it, then drop to the stylesheet's 0.
      clearTimeout(answer._faqTimer);
      answer.style.maxHeight = answer.scrollHeight + 'px';
      void answer.offsetHeight;
      answer.style.maxHeight = '';
    }
  }

  function toggle(item) {
    setOpen(item, !isOpen(item));
  }

  // Delegated so questions rendered after load still respond.
  document.addEventListener('click', function (event) {
    var question = event.target.closest('.faq-question');
    if (!question) return;

    // Answers can contain links (refund policy, mailto). Let those through.
    if (event.target.closest('a')) return;

    var item = question.closest('.' + ITEM);
    if (!item) return;

    event.preventDefault();
    toggle(item);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    var question = event.target.closest('.faq-question');
    if (!question) return;

    var item = question.closest('.' + ITEM);
    if (!item) return;

    event.preventDefault(); // Space would otherwise scroll the page.
    toggle(item);
  });

  function init() {
    var questions = document.querySelectorAll('.faq-question');
    if (!questions.length) return;

    Array.prototype.forEach.call(questions, function (question, index) {
      var item = question.closest('.' + ITEM);
      if (!item) return;

      question.setAttribute('role', 'button');
      question.setAttribute('tabindex', '0');
      question.setAttribute('aria-expanded', isOpen(item) ? 'true' : 'false');

      var answer = answerFor(item);
      if (answer) {
        if (!answer.id) answer.id = 'faq-answer-' + (index + 1);
        question.setAttribute('aria-controls', answer.id);
        answer.setAttribute('role', 'region');
      }

      // Honour any item the content author marked as open to begin with. No
      // animation is wanted on load, so the cap comes straight off.
      if (isOpen(item) && answer) {
        answer.classList.add(OPEN);
        answer.style.maxHeight = 'none';
      }
    });
  }

  // The normal path for lifting the height cap after the opening animation.
  document.addEventListener(
    'transitionend',
    function (event) {
      if (event.propertyName !== 'max-height') return;

      var answer = event.target;
      if (!answer.classList || !answer.classList.contains('faq-answer')) return;
      if (!answer.closest('.' + ITEM)) return;

      clearTimeout(answer._faqTimer);
      release(answer);
    },
    true
  );

  // No resize handling is needed: an open panel is left uncapped, so it
  // reflows with its content on its own.

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
